import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import AssignClientDropdown from './AssignClientDropdown';
import SearchClientsModal from './SearchClientsModal';
import AssignPhoneConfirmModal from './AssignPhoneConfirmModal';
import MergeTicketsModal from '../MergeTicketsModal';

const DROPDOWN_PAGE_SIZE = 4;
const MODAL_PAGE_SIZE = 5;

const AssignClientSelector = ({ ticket, onTicketUpdated, triggerRef, hideButton = false }) => {
  const { token, nombreCompleto, idUsuario } = useToken();
  const { showErrorToast, showSuccessToast } = useToast();

  // Dropdown State (Búsqueda rápida con scroll infinito)
  const [showAssignDropdown, setShowAssignDropdown] = useState(false);
  const [matchingClients, setMatchingClients] = useState([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreClients, setHasMoreClients] = useState(true);
  const [dropdownPage, setDropdownPage] = useState(1);
  const [dropdownSearch, setDropdownSearch] = useState('');

  // Modal State (Búsqueda completa paginada)
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [modalSearchTerm, setModalSearchTerm] = useState('');
  const [modalClients, setModalClients] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalPage, setModalPage] = useState(1);
  const [modalTotalPages, setModalTotalPages] = useState(1);
  const [modalTotalRecords, setModalTotalRecords] = useState(0);

  // Modal Confirmación Asignar Teléfono Adicional
  const [showPhoneConfirmModal, setShowPhoneConfirmModal] = useState(false);
  const [pendingClient, setPendingClient] = useState(null);
  const [previousOwner, setPreviousOwner] = useState(null);
  const [isAssigning, setIsAssigning] = useState(false);

  // Modal Combinar Tickets
  const [existingOpenTicket, setExistingOpenTicket] = useState(null);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [isMerging, setIsMerging] = useState(false);

  const dropdownRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const lastFetchedDropdownSearchRef = useRef(null);
  const lastFetchedModalSearchRef = useRef(null);

  const ticketPhone = ticket?.vTelefono || ticket?.phone || ticket?.ticketPhone || '';

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowAssignDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cargar clientes para el dropdown
  const loadDropdownClients = async (query, page = 1, append = false) => {
    if (page === 1) {
      setIsLoadingClients(true);
    } else {
      setIsLoadingMore(true);
    }

    try {
      const url = `/api/SupportEngine/GetClientsByPhone?phone=${encodeURIComponent(query || '')}&pageNumber=${page}&pageSize=${DROPDOWN_PAGE_SIZE}`;
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + token
        }
      });
      if (response.ok) {
        const result = await response.json();
        const clients = result.data || result;
        const list = Array.isArray(clients) ? clients : [];

        if (append) {
          setMatchingClients(prev => [...prev, ...list]);
        } else {
          setMatchingClients(list);
        }

        setHasMoreClients(list.length >= DROPDOWN_PAGE_SIZE);
      }
    } catch (err) {
      console.error('Error al cargar clientes coincidentes:', err);
    } finally {
      setIsLoadingClients(false);
      setIsLoadingMore(false);
    }
  };

  // Cargar clientes y paginación en el Modal
  const loadModalData = async (query, page = 1) => {
    setModalLoading(true);
    try {
      const trimmedQuery = query.trim();
      const searchUrl = `/api/SupportEngine/SearchClientsPaged?searchTerm=${encodeURIComponent(trimmedQuery)}&pageNumber=${page}&pageSize=${MODAL_PAGE_SIZE}`;
      const paginationUrl = `/api/SupportEngine/GetClientsPagination?searchTerm=${encodeURIComponent(trimmedQuery)}&pageSize=${MODAL_PAGE_SIZE}`;

      const [searchResp, paginationResp] = await Promise.all([
        fetch(searchUrl, {
          headers: {
            'Accept': 'application/json',
            'Authorization': 'Bearer ' + token
          }
        }),
        fetch(paginationUrl, {
          headers: {
            'Accept': 'application/json',
            'Authorization': 'Bearer ' + token
          }
        })
      ]);

      if (searchResp.ok) {
        const result = await searchResp.json();
        const clients = result.data || result;
        setModalClients(Array.isArray(clients) ? clients : []);
      } else {
        setModalClients([]);
      }

      if (paginationResp.ok) {
        const pagResult = await paginationResp.json();
        const pagData = pagResult.data || pagResult;
        const total = typeof pagData.totalRecords === 'number' ? pagData.totalRecords : 0;
        const pages = typeof pagData.totalPages === 'number' ? pagData.totalPages : 1;
        setModalTotalRecords(total);
        setModalTotalPages(Math.max(1, pages));
      } else {
        setModalTotalRecords(0);
        setModalTotalPages(1);
      }
    } catch (err) {
      console.error('Error al cargar clientes del modal:', err);
      setModalClients([]);
      setModalTotalRecords(0);
      setModalTotalPages(1);
    } finally {
      setModalLoading(false);
    }
  };

  // Toggle dropdown
  const handleToggleDropdown = () => {
    setShowAssignDropdown(prev => {
      const nextState = !prev;
      if (nextState) {
        const initialQuery = ticketPhone ? ticketPhone.slice(-10) : '';
        setDropdownSearch(initialQuery);
        setDropdownPage(1);
        lastFetchedDropdownSearchRef.current = initialQuery;
        loadDropdownClients(initialQuery, 1, false);
      }
      return nextState;
    });
  };

  // Abrir trigger desde prop externa si se envía triggerRef (como función directa y como .open())
  useEffect(() => {
    if (triggerRef) {
      const triggerFn = () => handleToggleDropdown();
      triggerFn.open = () => handleToggleDropdown();
      triggerRef.current = triggerFn;
    }
  });

  // Manejar cambio en el buscador rápido (debounce de 300ms)
  useEffect(() => {
    if (!showAssignDropdown) return;
    const trimmed = dropdownSearch.trim();
    if (lastFetchedDropdownSearchRef.current === trimmed) return;

    const timer = setTimeout(() => {
      lastFetchedDropdownSearchRef.current = trimmed;
      setDropdownPage(1);
      loadDropdownClients(trimmed, 1, false);
    }, 300);

    return () => clearTimeout(timer);
  }, [dropdownSearch, showAssignDropdown]);

  // Manejar scroll infinito del dropdown
  const handleDropdownScroll = () => {
    const container = scrollContainerRef.current;
    if (!container || isLoadingClients || isLoadingMore || !hasMoreClients) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollTop + clientHeight >= scrollHeight - 15) {
      const nextPage = dropdownPage + 1;
      setDropdownPage(nextPage);
      loadDropdownClients(dropdownSearch.trim(), nextPage, true);
    }
  };

  // Abrir Modal de Búsqueda Completa
  const handleOpenModal = () => {
    setShowAssignDropdown(false);
    setShowSearchModal(true);
    const initialQuery = ticketPhone ? ticketPhone.slice(-10) : '';
    setModalSearchTerm(initialQuery);
    setModalPage(1);
    lastFetchedModalSearchRef.current = initialQuery;
    loadModalData(initialQuery, 1);
  };

  // Cerrar Modal de Búsqueda Completa
  const handleCloseModal = () => {
    setShowSearchModal(false);
  };

  // Manejar cambio en buscador del Modal (debounce de 350ms)
  useEffect(() => {
    if (!showSearchModal) return;
    const trimmed = modalSearchTerm.trim();
    if (lastFetchedModalSearchRef.current === trimmed) return;

    const timer = setTimeout(() => {
      lastFetchedModalSearchRef.current = trimmed;
      setModalPage(1);
      loadModalData(trimmed, 1);
    }, 350);

    return () => clearTimeout(timer);
  }, [modalSearchTerm, showSearchModal]);

  // Paginación en Modal
  const handlePrevPage = () => {
    if (modalPage > 1 && !modalLoading) {
      const prev = modalPage - 1;
      setModalPage(prev);
      loadModalData(modalSearchTerm.trim(), prev);
    }
  };

  const handleNextPage = () => {
    if (modalPage < modalTotalPages && !modalLoading) {
      const next = modalPage + 1;
      setModalPage(next);
      loadModalData(modalSearchTerm.trim(), next);
    }
  };

  const handleGoToPage = pageNum => {
    if (pageNum >= 1 && pageNum <= modalTotalPages && pageNum !== modalPage && !modalLoading) {
      setModalPage(pageNum);
      loadModalData(modalSearchTerm.trim(), pageNum);
    }
  };

  // Seleccionar cliente: primero verifica si el cliente ya tiene un ticket abierto
  const handleSelectClient = async client => {
    if (!client || !ticket || isAssigning || isMerging) return;

    const ticketId = ticket.idTicket || ticket.id;
    const clientId = client.idClienteAkasia || client.id || client.customerId;

    if (!clientId) {
      proceedToPhoneAssignment(client);
      return;
    }

    // 1. Consultar si el cliente ya tiene un ticket abierto/activo
    try {
      const checkResp = await fetch(
        `/api/SupportEngine/CheckClientOpenTicket?idClienteAkasia=${encodeURIComponent(clientId)}&idExcludeTicket=${encodeURIComponent(ticketId)}`,
        {
          headers: {
            'Accept': 'application/json',
            'Authorization': 'Bearer ' + token
          }
        }
      );

      if (checkResp.ok) {
        const checkData = await checkResp.json();
        const respData = checkData.data || checkData;
        const openTicket = respData?.ticket || respData?.Ticket || respData;
        const etiquetasList = respData?.etiquetas || respData?.Etiquetas || openTicket?.etiquetas || [];

        if (openTicket && (openTicket.idTicket || openTicket.IdTicket)) {
          // Normalizar formato de openTicket si viene con mayúsculas/minúsculas o anidado
          const normalized = {
            idTicket: openTicket.idTicket || openTicket.IdTicket,
            vTelefono: openTicket.vTelefono || openTicket.VTelefono,
            iEstadoTicket: openTicket.iEstadoTicket || openTicket.IEstadoTicket,
            vAsunto: openTicket.vAsunto || openTicket.VAsunto,
            idCatNivelSoporte: openTicket.idCatNivelSoporte || openTicket.IdCatNivelSoporte,
            vNivelSoporte: openTicket.vNivelSoporte || openTicket.VNivelSoporte,
            idUsuarioAsignado: openTicket.idUsuarioAsignado || openTicket.IdUsuarioAsignado,
            vNombreAgenteAsignado: openTicket.vNombreAgenteAsignado || openTicket.VNombreAgenteAsignado,
            dCreatedDate: openTicket.dCreatedDate || openTicket.DCreatedDate,
            dUltimaActividad: openTicket.dUltimaActividad || openTicket.DUltimaActividad,
            etiquetas: etiquetasList
          };

          setPendingClient(client);
          setExistingOpenTicket(normalized);
          setShowAssignDropdown(false);
          setShowSearchModal(false);
          setShowMergeModal(true);
          return;
        }

      }
    } catch (err) {
      console.error('Error al verificar tickets abiertos del cliente:', err);
    }

    // 2. Si no tiene ticket abierto, proceder al flujo normal de asignación/confirmación de teléfono
    proceedToPhoneAssignment(client);
  };


  // Flujo normal de asignación y verificación de titularidad de número
  const proceedToPhoneAssignment = async client => {
    const cleanPhone = ticketPhone ? ticketPhone.replace(/[^0-9]/g, '') : '';

    if (cleanPhone) {
      setPendingClient(client);

      try {
        const phoneQuery = cleanPhone.length >= 10 ? cleanPhone.slice(-10) : cleanPhone;
        const response = await fetch(`/api/SupportEngine/GetClientsByPhone?phone=${encodeURIComponent(phoneQuery)}&pageNumber=1&pageSize=10`, {
          headers: {
            'Accept': 'application/json',
            'Authorization': 'Bearer ' + token
          }
        });

        if (response.ok) {
          const result = await response.json();
          const list = result.data || result;
          const clients = Array.isArray(list) ? list : [];

          // Si el teléfono ya le pertenece a este mismo cliente, no hace falta preguntar
          const alreadyBelongsToThisClient = clients.some(
            c => Number(c.idClienteAkasia) === Number(client.idClienteAkasia)
          );
          if (alreadyBelongsToThisClient) {
            executeAssign(client, false);
            return;
          }

          // Si le pertenece a otro cliente distinto (transferencia de titularidad)
          const otherClient = clients.find(
            c => Number(c.idClienteAkasia) !== Number(client.idClienteAkasia)
          );
          setPreviousOwner(otherClient || null);
          setShowPhoneConfirmModal(true);
          return;
        }
      } catch (err) {
        console.error('Error al consultar dueño del teléfono:', err);
      }

      setPreviousOwner(null);
      setShowPhoneConfirmModal(true);
    } else {
      executeAssign(client, false);
    }
  };

  // Confirmar Combinación de Tickets
  const handleConfirmMerge = async () => {
    if (!existingOpenTicket || !ticket || isMerging) return;
    const currentTicketId = Number(ticket.idTicket || ticket.id);
    const targetTicketId = Number(existingOpenTicket.idTicket || existingOpenTicket.id);
    const sourcePhone = ticket?.vTelefono || ticket?.phone || ticket?.ticketPhone;
    const targetPhone = existingOpenTicket?.vTelefono || existingOpenTicket?.phone || existingOpenTicket?.ticketPhone || sourcePhone;

    setIsMerging(true);
    try {
      const response = await fetch('/api/SupportEngine/MergeTickets', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify({
          idTicketSource: currentTicketId,
          idTicketTarget: targetTicketId,
          idUsuario: idUsuario ? Number(idUsuario) : null,
          agentUser: nombreCompleto || null
        })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        showErrorToast(errData.message || 'Error al combinar los tickets.');
        return;
      }

      showSuccessToast(`Ticket #${currentTicketId} combinado exitosamente en el Ticket #${targetTicketId}.`);
      setShowMergeModal(false);
      setExistingOpenTicket(null);
      setPendingClient(null);

      // 1. Agregar notas internas a ambos tickets
      try {
        await fetch('/api/SupportEngine/CreateTicketNote', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: 'Bearer ' + token
          },
          body: JSON.stringify({
            ticketId: currentTicketId,
            note: `Este ticket ha sido combinado con el ticket #${targetTicketId}. Ticket principal ticket #${targetTicketId}`,
            noteColor: '#dbeafe'
          })
        });

        await fetch('/api/SupportEngine/CreateTicketNote', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: 'Bearer ' + token
          },
          body: JSON.stringify({
            ticketId: targetTicketId,
            note: `Se ha combinado el ticket #${currentTicketId} con este ticket. Ticket principal ticket #${targetTicketId}`,
            noteColor: '#dbeafe'
          })
        });
      } catch (noteErr) {
        console.warn('No se pudieron crear las notas de combinación:', noteErr);
      }

      // 2. Enviar mensaje por WhatsApp a ambos tickets
      try {
        if (sourcePhone) {
          await fetch('/api/SupportEngine/SendTicketMessage', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              Authorization: 'Bearer ' + token
            },
            body: JSON.stringify({
              idTicket: currentTicketId,
              toPhoneNumber: sourcePhone,
              message: `Este ticket ha sido combinado con el ticket #${targetTicketId}. Ticket principal ticket #${targetTicketId}`,
              idUsuario: idUsuario ? Number(idUsuario) : null
            })
          });
        }

        if (targetPhone) {
          await fetch('/api/SupportEngine/SendTicketMessage', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              Authorization: 'Bearer ' + token
            },
            body: JSON.stringify({
              idTicket: targetTicketId,
              toPhoneNumber: targetPhone,
              message: `El ticket #${currentTicketId} ha sido combinado con este ticket. Ticket principal ticket #${targetTicketId}`,
              idUsuario: idUsuario ? Number(idUsuario) : null
            })
          });
        }
      } catch (msgErr) {
        console.warn('No se pudieron enviar los mensajes de combinación:', msgErr);
      }

      window.dispatchEvent(new CustomEvent('akasia:support:ticket_activity', {
        detail: {
          idTicket: currentTicketId,
          iEstadoTicket: 4,
          dUltimaActividad: new Date().toISOString()
        }
      }));

      if (onTicketUpdated) {
        onTicketUpdated();
      }
    } catch (err) {
      console.error('Error en MergeTickets:', err);
      showErrorToast('Error al procesar la combinación de tickets.');
    } finally {
      setIsMerging(false);
    }
  };

  // Solo Asignar desde el Modal de Merge (No combinar): asigna directamente sin abrir otro modal
  const handleAssignOnly = () => {
    setShowMergeModal(false);
    const clientToAssign = pendingClient;
    setExistingOpenTicket(null);
    if (clientToAssign) {
      executeAssign(clientToAssign, true);
    }
  };


  // Cancelar: Cierra el modal y no se asigna nada
  const handleCancelAssignment = () => {
    setShowPhoneConfirmModal(false);
    setPendingClient(null);
    setPreviousOwner(null);
  };

  // Confirmar: Asigna el cliente al ticket Y el número a los adicionales del cliente (opción "Sí")
  const handleConfirmAssignment = async () => {
    if (!pendingClient || isAssigning) return;
    const clientToAssign = pendingClient;
    setShowPhoneConfirmModal(false);
    setPendingClient(null);
    setPreviousOwner(null);
    await executeAssign(clientToAssign, true);
  };

  // Rechazar número adicional: Asigna el cliente al ticket SIN agregar el número a adicionales (opción "No")
  const handleRejectAssignment = async () => {
    if (!pendingClient || isAssigning) return;
    const clientToAssign = pendingClient;
    setShowPhoneConfirmModal(false);
    setPendingClient(null);
    setPreviousOwner(null);
    await executeAssign(clientToAssign, false);
  };

  // Ejecuta la llamada al backend para asignar el cliente y opcionalmente el teléfono
  const executeAssign = async (client, assignPhone = false) => {
    if (!client || !ticket || isAssigning) return;
    const ticketId = ticket.idTicket || ticket.id;
    if (!ticketId) return;

    setIsAssigning(true);

    try {
      // 1. Asignar cliente al ticket
      const response = await fetch('/api/SupportEngine/AssignTicketClient', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify({
          idTicket: parseInt(ticketId, 10),
          idClienteAkasia: client.idClienteAkasia
        })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        showErrorToast(errData.message || 'Error al asignar el cliente al ticket.');
        return;
      }

      // 2. Si se confirmó asignar el número para futuros contactos, registrar/transferir en adicionales
      if (assignPhone) {
        try {
          await fetch('/api/SupportEngine/AssignTicketPhoneToCustomer', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Authorization': 'Bearer ' + token
            },
            body: JSON.stringify({
              idTicket: parseInt(ticketId, 10),
              idClienteAkasia: parseInt(client.idClienteAkasia, 10)
            })
          });
        } catch (phoneErr) {
          console.error('Error al registrar teléfono adicional:', phoneErr);
        }
      }

      const result = await response.json();
      const updated = result.data || result;

      showSuccessToast(
        assignPhone && previousOwner
          ? `Número de teléfono transferido y asignado a ${client.vNombreCompleto || 'Cliente'}`
          : `Número de teléfono asignado al cliente ${client.vNombreCompleto || ''}`.trim()
      );

      setShowPhoneConfirmModal(false);
      setShowAssignDropdown(false);
      setShowSearchModal(false);
      setPendingClient(null);
      setPreviousOwner(null);

      if (onTicketUpdated) {
        onTicketUpdated(updated);
      }
    } catch (err) {
      showErrorToast('Error de red al asignar cliente: ' + err.message);
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <div className="d-inline-flex" ref={dropdownRef}>
      {/* Botón trigger para abrir selector */}
      {!hideButton && (
        <button
          type="button"
          className="btn btn-link text-muted p-1 text-decoration-none rounded-circle hover-bg-200"
          style={{ width: '26px', height: '26px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={handleToggleDropdown}
          title={ticket.idClienteAkasia ? 'Cambiar cliente asignado' : 'Asignar cliente a este ticket'}
        >
          <FontAwesomeIcon
            icon={ticket.idClienteAkasia ? faEdit : faUserPlus}
            className={ticket.idClienteAkasia ? 'text-secondary' : 'text-primary'}
            style={{ fontSize: '0.85rem' }}
          />
        </button>
      )}

      {/* Subcomponente: Dropdown flotante con scroll infinito */}
      <AssignClientDropdown
        show={showAssignDropdown}
        search={dropdownSearch}
        onSearchChange={setDropdownSearch}
        clients={matchingClients}
        isLoading={isLoadingClients}
        isLoadingMore={isLoadingMore}
        selectedClientId={ticket.idClienteAkasia}
        ticketPhone={ticketPhone}
        onScroll={handleDropdownScroll}
        onSelectClient={handleSelectClient}
        onOpenModal={handleOpenModal}
        onClose={() => setShowAssignDropdown(false)}
        scrollRef={scrollContainerRef}
      />

      {/* Subcomponente: Modal de búsqueda completa paginada */}
      <SearchClientsModal
        show={showSearchModal}
        ticketId={ticket.idTicket || ticket.id}
        searchTerm={modalSearchTerm}
        onSearchTermChange={setModalSearchTerm}
        clients={modalClients}
        isLoading={modalLoading}
        page={modalPage}
        totalPages={modalTotalPages}
        totalRecords={modalTotalRecords}
        onPrevPage={handlePrevPage}
        onNextPage={handleNextPage}
        onGoToPage={handleGoToPage}
        onSelectClient={handleSelectClient}
        isAssigning={isAssigning}
        selectedClientId={ticket.idClienteAkasia}
        onClose={handleCloseModal}
      />

      {/* Subcomponente: Modal de confirmación de titularidad de número (Sí / No) */}
      <AssignPhoneConfirmModal
        show={showPhoneConfirmModal}
        previousOwner={previousOwner}
        pendingClient={pendingClient}
        ticketPhone={ticketPhone}
        isAssigning={isAssigning}
        onConfirm={handleConfirmAssignment}
        onReject={handleRejectAssignment}
        onCancel={handleCancelAssignment}
      />

      {/* Subcomponente: Modal de combinación de tickets cuando se detecta ticket abierto */}
      <MergeTicketsModal
        show={showMergeModal}
        onClose={() => {
          setShowMergeModal(false);
          setExistingOpenTicket(null);
          setPendingClient(null);
        }}
        client={pendingClient}
        currentTicket={ticket}
        existingTicket={existingOpenTicket}
        onConfirmMerge={handleConfirmMerge}
        onAssignOnly={handleAssignOnly}
        isProcessing={isMerging}
      />
    </div>
  );
};

export default AssignClientSelector;
